import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { toast } from 'sonner'
import { useDisclosure } from '@mantine/hooks'
import type { HttpErrorResponse } from '@/types/error'
import type { GetBookingDetailResponse } from '@/types/booking'
import ModalPopup from '@/components/ui/ModalPopup'
import useBookingMutation from '@/hooks/useBookingMutation'
import {
  getBookingQueryOptions,
  getBookingTransportationQueryOptions,
} from '@/queries/query'
import { isHttpError } from '@/utils/http'
import { queryKeys } from '@/queries/keys'

export const Route = createFileRoute(
  '/make-booking/$id/trip-details/$transportationId/delete',
)({
  component: RouteComponent,
})

function RouteComponent() {
  const router = useRouter()
  const navigate = Route.useNavigate()
  const queryClient = useQueryClient()
  const transportationId = Route.useParams({
    select: (params) => params.transportationId,
  })
  const [isLoading, setLoading] = useDisclosure()
  const { deleteTransportation } = useBookingMutation()

  const {
    data: {
      data: { booked_transportation },
    },
  } = useSuspenseQuery(getBookingTransportationQueryOptions(transportationId))

  const onConfirm = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setLoading.open()
    return deleteTransportation.mutate(
      {
        bookingId: booked_transportation.adhoc_booking_id,
        bookedTransportationId: booked_transportation.id,
      },
      {
        onSuccess: async (_, payload) => {
          toast.success(
            `${booked_transportation.adhoc_transportation_type.name} successfully removed`,
          )
          try {
            const resBooking = await queryClient.fetchQuery(
              getBookingQueryOptions(payload.bookingId),
            )
            queryClient.setQueryData<GetBookingDetailResponse>(
              queryKeys.booking.detail(booked_transportation.adhoc_booking_id),
              resBooking,
            )
          } catch (error) {
            setLoading.close()
            if (isHttpError(error, 404)) {
              return navigate({
                ignoreBlocker: true,
                replace: true,
                to: '/',
              })
            }
          }
          setLoading.close()
          return router.history.back({ ignoreBlocker: true })
        },
        onError: async (err) => {
          setLoading.close()
          if (isHttpError(err)) {
            const res = await err.response.json<HttpErrorResponse>()
            return toast.error('message' in res ? res.message : res.error)
          }
          return toast.error('Something went wrong')
        },
      },
    )
  }
  return (
    <ModalPopup
      opened
      onClose={() => router.history.back({ ignoreBlocker: true })}
      onConfirm={onConfirm}
      isPending={isLoading}
      title="Remove Bus?"
      description="Are you sure you want to remove this bus from your booking? This action cannot be undone."
      bgColor="#D21C1C"
      confirmLabel="Remove Bus"
    />
  )
}
